import type { ButtonProps, ChipProps, SxProps, Theme } from '@mui/material';
import { Button, Chip, Container, Stack, useMediaQuery } from '@mui/material';
import type { ReactNode } from 'react';
import BayerLookup from '/@/components/bayer/lookup';
import Breadcrumbs from '../breadcrumbs';

export interface HeaderProps {
    title: string | ReactNode;
    subtitle?: string | ReactNode;
    breadcrumbs?: Array<{
        label: string;
        onClick?: () => void;
        href?: string;
    }>;
    actions?: ReactNode[];
    buttons?: ButtonProps[];
    showSkeleton?: boolean;
    chips?: ChipProps[];
    sx?: SxProps;
    variant?: 'default' | 'full-gray';
    mobileHeader?: ReactNode;
    titleIcon?: ReactNode;
}

const Header = ({
    mobileHeader,
    title,
    subtitle,
    breadcrumbs,
    actions = [],
    buttons = [],
    chips = [],
    showSkeleton = false,
    sx,
    variant = 'default',
    titleIcon,
}: HeaderProps) => {
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const renderHeader = () => {
        return (
            <Stack spacing={1} sx={sx}>
                {mobileHeader && isSmOrLower && mobileHeader}
                {breadcrumbs && breadcrumbs.length > 0 && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems='start'>
                        <Stack direction='column' spacing={3}>
                            <Breadcrumbs breadcrumbs={breadcrumbs} />
                        </Stack>
                    </Stack>
                )}
                <Stack direction='row' spacing={1} justifyContent='space-between' flexWrap='wrap' useFlexGap>
                    <BayerLookup
                        title={title}
                        body={subtitle}
                        titleIcon={titleIcon}
                        showSkeleton={showSkeleton}
                        sx={{
                            maxWidth: { xs: '100%', sm: '100%' },
                        }}
                    />
                    {(actions.length > 0 || buttons.length > 0) && (
                        <Stack mt={{ xs: 1, sm: 0 }} width={{ xs: '100%', sm: 'auto' }} sx={{ ml: 'auto' }}>
                            <Stack
                                spacing={2}
                                direction='row'
                                alignItems='center'
                                justifyContent='center'
                                flexWrap='wrap'
                                useFlexGap
                            >
                                {buttons.map((button, index) => (
                                    <Button
                                        key={`button-${
                                            // biome-ignore lint/suspicious/noArrayIndexKey: it's okay
                                            index
                                        }`}
                                        {...button}
                                    />
                                ))}
                                {actions.map((action) => action)}
                            </Stack>
                        </Stack>
                    )}
                </Stack>
                {chips.length > 0 && (
                    <Stack direction='row' spacing={1}>
                        {chips.map((chip) => (
                            <Chip
                                key={`chip-${chip.label}`}
                                {...chip}
                                sx={{
                                    fontSize: (t: any) => t.typography.caption.fontSize,
                                    fontWeight: 700,
                                    ...chip.sx,
                                }}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
        );
    };

    if (variant === 'full-gray') {
        return (
            <Stack sx={{ backgroundColor: '#f2f2f2' }}>
                <Container maxWidth={'xl'} sx={{ pb: 4, pt: 5 }}>
                    {renderHeader()}
                </Container>
            </Stack>
        );
    }

    return renderHeader();
};

export default Header;
